import clsx from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/Button/Button";
import { AppText } from "@/components/Text/AppText";

export interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    className?: string;
    variant?: "default" | "compact" | "minimal";
    showFirstLast?: boolean;
    maxVisiblePages?: number;
}

export const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    className,
    variant = "default",
    showFirstLast = false,
    maxVisiblePages = 5,
}: PaginationProps) => {
    // Don't render if there's only one page or no pages
    if (totalPages <= 1) return null;

    // Calculate which pages to show
    const getVisiblePages = () => {
        if (totalPages <= maxVisiblePages) {
            // If total pages is less than or equal to max visible, show all pages
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        if (currentPage <= Math.ceil(maxVisiblePages / 2)) {
            // Show first maxVisiblePages when near the beginning
            return Array.from({ length: maxVisiblePages }, (_, i) => i + 1);
        }

        if (currentPage >= totalPages - Math.floor(maxVisiblePages / 2)) {
            // Show last maxVisiblePages when near the end
            return Array.from(
                { length: maxVisiblePages },
                (_, i) => totalPages - maxVisiblePages + i + 1,
            );
        }

        // Show current page ± (maxVisiblePages / 2) when in the middle
        const halfVisible = Math.floor(maxVisiblePages / 2);
        return Array.from(
            { length: maxVisiblePages },
            (_, i) => currentPage - halfVisible + i,
        );
    };

    const visiblePages = getVisiblePages();

    const renderPageButton = (page: number, isActive: boolean = false) => {
        if (variant === "minimal") {
            return (
                <button
                    key={page}
                    onClick={() => onPageChange(page)}
                    className={clsx(
                        "h-8 w-8 rounded-md text-sm font-medium transition-colors",
                        isActive
                            ? "bg-primary-700 text-white"
                            : "text-neutral-600 hover:bg-neutral-100",
                    )}
                >
                    <AppText
                        variant="regular"
                        className="font-medium"
                    >
                        {page}
                    </AppText>
                </button>
            );
        }

        return (
            <Button
                key={page}
                variant={isActive ? "solid" : "outline"}
                color="primary"
                onClick={() => onPageChange(page)}
                className={clsx(
                    variant === "compact" ? "h-8 w-8 p-0" : "h-10 w-10 p-0",
                    isActive && "bg-primary-700",
                )}
            >
                <AppText
                    variant="labelsbuttons"
                    className={clsx(
                        isActive ? "text-white" : "text-primary-700",
                    )}
                >
                    {page}
                </AppText>
            </Button>
        );
    };

    const renderNavigationButton = (
        onClick: () => void,
        disabled: boolean,
        icon: React.ReactNode,
        ariaLabel: string,
    ) => {
        if (variant === "minimal") {
            return (
                <button
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={ariaLabel}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50"
                >
                    {icon}
                </button>
            );
        }

        return (
            <Button
                variant="outline"
                color="primary"
                onClick={onClick}
                disabled={disabled}
                className={clsx(
                    variant === "compact" ? "h-8 w-8 p-0" : "h-10 w-10 p-0",
                )}
                aria-label={ariaLabel}
            >
                {icon}
            </Button>
        );
    };

    return (
        <div
            className={clsx(
                "flex items-center justify-center gap-2",
                variant === "minimal" && "space-x-2",
                className,
            )}
        >
            {/* First page button */}
            {showFirstLast &&
                totalPages > maxVisiblePages &&
                renderNavigationButton(
                    () => onPageChange(1),
                    currentPage === 1,
                    <ChevronLeft className="h-4 w-4" />,
                    "First page",
                )}

            {/* Previous page button */}
            {renderNavigationButton(
                () => onPageChange(currentPage - 1),
                currentPage === 1,
                <ChevronLeft className="h-4 w-4" />,
                "Previous page",
            )}

            {/* Page numbers */}
            {visiblePages.map(page =>
                renderPageButton(page, page === currentPage),
            )}

            {/* Next page button */}
            {renderNavigationButton(
                () => onPageChange(currentPage + 1),
                currentPage === totalPages,
                <ChevronRight className="h-4 w-4" />,
                "Next page",
            )}

            {/* Last page button */}
            {showFirstLast &&
                totalPages > maxVisiblePages &&
                renderNavigationButton(
                    () => onPageChange(totalPages),
                    currentPage === totalPages,
                    <ChevronRight className="h-4 w-4" />,
                    "Last page",
                )}
        </div>
    );
};
